import { fmtDMHM } from "@/lib/traffic";
import { cn } from "@/lib/utils";

const t = (iso) => new Date(iso).getTime();

function ticks(since, until, days) {
  const n = 6, out = [];
  for (let i = 0; i <= n; i++) {
    const ms = since + ((until - since) * i) / n;
    const d = new Date(ms);
    out.push({ pct: (i / n) * 100, label: days <= 1 ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : days <= 7 ? d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) });
  }
  return out;
}

// One bar per radar: green = reachable, red = outage (ping no-reply or radar-server offline), hatched = ongoing.
export function ReachabilityChart({ data }) {
  if (!data) return <div className="h-40 animate-pulse rounded-xl bg-secondary/50" />;
  const since = t(data.since), until = t(data.until), span = Math.max(1, until - since);
  const axis = ticks(since, until, data.days);
  const pos = (iso) => Math.max(0, Math.min(100, ((t(iso) - since) / span) * 100));
  return (
    <div data-testid="reachability-chart">
      <div className="ml-[150px] mr-2 flex justify-between text-[10px] text-muted-foreground">
        {axis.map((a, i) => <span key={i} className="num" style={{ transform: i === 0 ? "none" : i === axis.length - 1 ? "translateX(0)" : "translateX(-50%)" }}>{a.label}</span>)}
      </div>
      <div className="mt-1 space-y-1">
        {data.sites.map((s) => (
          <div key={s.site} className="flex items-center gap-2" data-testid={`reach-row-${s.site}`}>
            <div className="w-[142px] shrink-0 truncate text-xs"><span className="font-medium">{s.site}</span> <span className="text-muted-foreground">{s.name}</span></div>
            <div className={cn("relative h-4 flex-1 overflow-hidden rounded-sm", s.vendor_online == null && !s.timeline.length ? "bg-emerald-500/50" : "bg-emerald-500/80")} title={`${s.site} · ${s.availability_pct}% available · ${s.downtime_minutes} min downtime`}>
              {axis.slice(1, -1).map((a, i) => <div key={i} className="absolute inset-y-0 w-px bg-background/40" style={{ left: `${a.pct}%` }} />)}
              {s.timeline.map((o, i) => {
                const left = pos(o.from), right = o.to ? pos(o.to) : 100;
                return <div key={i} data-testid={`reach-outage-${s.site}-${i}`} className={cn("absolute inset-y-0 bg-rose-500", !o.to && "animate-pulse")}
                  style={{ left: `${left}%`, width: `${Math.max(0.35, right - left)}%` }}
                  title={`${s.site} outage · ${fmtDMHM(o.from)} → ${o.to ? fmtDMHM(o.to) : "ongoing"} · ${o.minutes} min · ${o.source === "ping" ? "no ping reply" : "radar server log"}`} />;
              })}
            </div>
            <div className={cn("num w-14 shrink-0 text-right text-xs", s.downtime_minutes > 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground")} data-testid={`reach-avail-${s.site}`}>{s.availability_pct}%</div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-emerald-500/80" /> Reachable</span>
        <span className="flex items-center gap-1"><span className="h-2 w-3 rounded-sm bg-rose-500" /> Outage (no ping reply / radar server offline)</span>
        <span className="flex items-center gap-1"><span className="h-2 w-3 animate-pulse rounded-sm bg-rose-500" /> Ongoing</span>
        <span className="ml-auto">Availability % = share of the window not in an outage</span>
      </div>
    </div>
  );
}
